/**
 * 個別説明 (briefings) 親ページ。
 * タブ: 申込 / 確定 / 完了 / スケジュール / 実施状況 (管理者のみ、2026-10-07) / 設定 (右端)
 * 表示中のタブは URL (?tab=) に持ち、記録ページから戻ると同じタブに戻る。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §7
 */
import {
  BRIEFING_DELETED_SLOT_LABEL,
  BRIEFING_STYLE_LABEL,
  resolveBriefingStyle,
} from '@goenhub/shared';
import { SETTINGS_BRIEFING_DEFAULTS, isBriefingSlotOpen } from '@goenhub/shared';
import {
  dealBriefingCandidates,
  dealBriefingSessions,
  dealPendingBriefingCandidates,
} from '@goenhub/shared';
import { useQuery } from '@tanstack/react-query';
import { collection, getDocs } from 'firebase/firestore';
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Edit,
  FileText,
  Inbox,
  Mail,
  MapPin,
  MessageSquare,
  Plus,
  Settings,
  Video,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { BriefingApplicationDetails } from './BriefingApplicationDetails';
import { BriefingCandidateDecisionDialog } from './BriefingCandidateDecisionDialog';
import { BriefingScheduleDialog } from './BriefingScheduleDialog';
import { BriefingStatusTab } from './BriefingStatusTab';
import { ConfirmBriefingDateDialog } from './ConfirmBriefingDateDialog';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useFeatureActions } from '@/features/permissions/usePermission';
import { useAdminDirectory } from '@/hooks/useAdmins';
import { useAuth } from '@/hooks/useAuth';
import {
  useBriefingContacts,
  useBriefingSchedules,
  useBriefingSettingsMap,
  useDeleteBriefingSchedule,
  useDeletedBriefingScheduleIds,
  useMyBriefingSettings,
  useSubmissionBriefingCandidateFallback,
  useUpdateMyBriefingSettings,
  type BriefingScheduleRecord,
} from '@/hooks/useBriefings';
import { useDeals, type DealRecord } from '@/hooks/useDeals';
import { db } from '@/lib/firebase';

function tsToDate(v: unknown): Date | null {
  if (!v) return null;
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate();
  }
  return null;
}

function fmtDate(d: Date | null): string {
  if (!d) return '—';
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * 全 briefing-stage Deal について、briefing_records サブコレクションの有無を一括取得。
 * deals の数が多い場合は最適化が必要だが、Phase 5 の MVP では並列取得で許容。
 */
function useBriefingRecordsFlags(deals: DealRecord[] | undefined) {
  const dealIds = (deals ?? []).map((d) => d.id).join(',');
  return useQuery({
    queryKey: ['briefing_records_flags', dealIds],
    enabled: (deals?.length ?? 0) > 0,
    // 完了扱いの deal → 'completed' (実施記録あり) or 'not_conducted' (未実施マークのみ)。
    // 下書き (status='draft') は完了扱いにしない (2026-07-24)。
    queryFn: async (): Promise<Map<string, 'completed' | 'not_conducted'>> => {
      const flags = new Map<string, 'completed' | 'not_conducted'>();
      const tasks = (deals ?? []).map(async (d) => {
        const snap = await getDocs(collection(db, 'deals', d.id, 'briefing_records'));
        const statuses = snap.docs.map(
          (r) => (r.data() as { status?: string }).status ?? 'completed',
        );
        if (statuses.some((s) => s === 'completed')) {
          flags.set(d.id, 'completed');
        } else if (statuses.some((s) => s === 'not_conducted')) {
          flags.set(d.id, 'not_conducted');
        }
      });
      await Promise.all(tasks);
      return flags;
    },
  });
}

type BriefingsTab = 'applications' | 'confirmed' | 'completed' | 'schedule' | 'settings' | 'status';
const BRIEFINGS_TABS: BriefingsTab[] = [
  'applications',
  'confirmed',
  'completed',
  'schedule',
  'settings',
  'status',
];

export function BriefingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { data: deals, isLoading } = useDeals();
  const { data: schedules, isLoading: schedulesLoading } = useBriefingSchedules();
  const deleteScheduleMutation = useDeleteBriefingSchedule();
  // 予約受付締切設定はユーザー個別 (briefing_settings/{uid})。各枠の assigneeUid の設定で判定。
  const assigneeUids = useMemo(
    () => (schedules ?? []).map((s) => s.assigneeUid).filter(Boolean),
    [schedules],
  );
  const { data: settingsMap } = useBriefingSettingsMap(assigneeUids);

  // v3.2.18: 外部営業ロール (level='external') は自分の担当案件のみ表示
  const isExternalSa = user?.level === 'external' && !user?.isOwner;

  // 自分の枠 id 集合。useBriefingSchedules は外部SA に対して
  // where('assigneeUid','==',uid) で「自分の枠だけ」を返すため、この Set がそのまま
  // 「自分の枠」を意味する (管理者では全枠になるが、下の判定は外部SA 限定なので影響しない)。
  const mySlotIds = useMemo(() => new Set((schedules ?? []).map((s) => s.id)), [schedules]);

  // スケジュール枠の操作可否。Cloud Function の権限判定と一致させる:
  // - 管理者 (owner / 外部SA以外) は role で常に全操作可。
  // - 外部SA は sa.briefings の create/edit/delete 権限で判定 (自分の枠のみ)。
  const isManagerUi = Boolean(user?.isOwner) || (!!user && user.level !== 'external');
  const saActions = useFeatureActions('sa.briefings');
  const scheduleActions = isManagerUi
    ? { view: true, create: true, edit: true, delete: true }
    : saActions;

  // briefing 系 Deal のみ。外部営業ロールなら自分が担当 (assigneeUid / salesAgentUid /
  // briefingAssigneeUid) の案件にさらに絞る。
  const briefingDeals = useMemo(() => {
    const uid = user?.uid;
    return (deals ?? []).filter((d) => {
      if (d.category !== 'explanation' || d.stage !== 'briefing') return false;
      if (isExternalSa) {
        if (!uid) return false;
        // 2026-08-18: 申込段階の Deal には SA タグが付かない。
        //   候補枠が複数SAにまたがりうるため「誰の顧客か」を申込時点で決められず、
        //   salesAgentUid は日程が確定した枠の担当者に対してのみ付く設計。
        //   そのため「自分の枠が pending 候補として選ばれている申込」も自分の案件として表示する。
        //   確定すると同一申込グループの pending は解消される (applyConfirmDecision) ので、
        //   他SAの枠で確定した申込はここから自動的に外れる。
        const mineByCandidate = dealPendingBriefingCandidates(d).some((c) =>
          mySlotIds.has(c.scheduleId),
        );
        // 複数セッション (2026-08-18+) で自分が 2 回目以降の担当のケース。
        // briefingAssigneeUid は代表セッションの担当しか指さないため個別に見る。
        const mineBySession = dealBriefingSessions(d).some((sess) => sess.assigneeUid === uid);
        if (
          d.assigneeUid !== uid &&
          d.salesAgentUid !== uid &&
          d.briefingAssigneeUid !== uid &&
          !mineByCandidate &&
          !mineBySession
        )
          return false;
      }
      return true;
    });
  }, [deals, isExternalSa, user?.uid, mySlotIds]);

  // 顧客名の解決用。外部SA 以外は全連絡先 (limit を付けると枠外の連絡先名が解決できず
  // 顧客名未確定になる)。外部SA は自分の顧客 + 自分に関係する申込の顧客 (2026-10-16)。
  const contacts = useBriefingContacts(briefingDeals);

  const { data: recordedFlags } = useBriefingRecordsFlags(briefingDeals);

  // 申込タブ = 「日程未確定」に加えて「確定済みだが未処理の新規申込 (pending 候補) がある」商談も含める。
  // 2026-08-18: 同一顧客が再申込すると high マージで確定済み商談に吸収され、
  //   どのタブにも新規申込として現れない実害があったため pending 候補も申込として扱う。
  const applications = briefingDeals.filter(
    (d) => !d.scheduledBriefingAt || dealPendingBriefingCandidates(d).length > 0,
  );
  const confirmed = briefingDeals.filter((d) => d.scheduledBriefingAt && !recordedFlags?.has(d.id));
  const completed = briefingDeals.filter((d) => recordedFlags?.has(d.id));

  // 旧申込 (Deal に候補未保存、2026-07-23 修正前) は form_submissions の回答から候補日を復元
  const scheduleIdList = useMemo(() => (schedules ?? []).map((s) => s.id), [schedules]);
  const fallbackTargets = useMemo(
    () => applications.filter((d) => dealBriefingCandidates(d).length === 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [briefingDeals],
  );
  const { data: fallbackCandidates } = useSubmissionBriefingCandidateFallback(
    fallbackTargets,
    scheduleIdList,
  );
  // 申込タブの候補日のうち削除済みの枠 (id の代わりに「削除された候補日」と表記する)
  const deletedScheduleIds = useDeletedBriefingScheduleIds(
    applications.flatMap((d) => dealPendingBriefingCandidates(d).map((c) => c.scheduleId)),
  );

  // 実施状況タブは部署「管理者」とオーナーのみ (外部SA は部署に関わらず不可)
  const canViewStatus =
    !!user && (user.isOwner || (user.dept === 'admin' && user.level !== 'external'));
  // タブは URL (?tab=) に持つ (記録ページから戻った時に同じタブへ戻すため)。
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as BriefingsTab | null;
  const tab: BriefingsTab =
    tabParam && BRIEFINGS_TABS.includes(tabParam) && (tabParam !== 'status' || canViewStatus)
      ? tabParam
      : 'applications';
  const setTab = (next: BriefingsTab) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'applications') p.delete('tab');
        else p.set('tab', next);
        return p;
      },
      { replace: true },
    );
  };
  /** 記録ページへ。戻り先として今の URL (タブ・絞り込み込み) を渡す */
  const openRecordPage = (dealId: string) =>
    navigate(`/briefings/record/${dealId}`, {
      state: { from: `${location.pathname}${location.search}` },
    });
  // 実施状況タブ: 記録ページから戻った時に強調する行
  const focusRowKey = (location.state as { focusRowKey?: string } | null)?.focusRowKey ?? null;
  const consumeFocusRow = useCallback(() => {
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [navigate, location.pathname, location.search]);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    dealId: string | null;
    candidateScheduleIds?: string[];
    /** 商談に紐づく SA。枠の絞り込みに使う */
    salesAgentUid?: string | null;
  }>({ open: false, dealId: null });
  // 候補日の確定/拒否ポップアップ (顧客選択の候補を自動紐付け)
  const [decisionDialog, setDecisionDialog] = useState<{
    open: boolean;
    dealId: string | null;
    candidates: ReturnType<typeof dealPendingBriefingCandidates>;
  }>({ open: false, dealId: null, candidates: [] });
  // 申込行の「▼」展開 (フォーム回答内容の表示)
  const [expandedDealIds, setExpandedDealIds] = useState<string[]>([]);
  const toggleExpanded = (dealId: string) =>
    setExpandedDealIds((prev) =>
      prev.includes(dealId) ? prev.filter((id) => id !== dealId) : [...prev, dealId],
    );
  const [scheduleDialog, setScheduleDialog] = useState<{
    open: boolean;
    existing: BriefingScheduleRecord | null;
  }>({ open: false, existing: null });

  // ─── 設定タブ: 予約受付締切 (自分の設定。平日/休日のリードタイム) ───
  const { merged: myBriefingSettings } = useMyBriefingSettings();
  const updateBriefingSettings = useUpdateMyBriefingSettings();
  const [weekdayLeadDays, setWeekdayLeadDays] = useState(0);
  const [holidayLeadDays, setHolidayLeadDays] = useState(0);
  useEffect(() => {
    setWeekdayLeadDays(myBriefingSettings.weekdayLeadDays);
    setHolidayLeadDays(myBriefingSettings.holidayLeadDays);
  }, [myBriefingSettings.weekdayLeadDays, myBriefingSettings.holidayLeadDays]);

  const renderSettingsTab = () => {
    const onSave = async () => {
      try {
        await updateBriefingSettings.mutateAsync({
          weekdayLeadDays: Math.max(0, Math.floor(Number(weekdayLeadDays) || 0)),
          holidayLeadDays: Math.max(0, Math.floor(Number(holidayLeadDays) || 0)),
        });
        toast.success('予約受付締切の設定を保存しました');
      } catch (err) {
        toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
      }
    };
    return (
      <div className="max-w-xl space-y-4">
        <div className="rounded-md border border-[var(--border)] p-4 space-y-4">
          <div>
            <div className="text-sm font-medium text-[var(--text-primary)]">
              予約受付の締切（リードタイム）— あなたの枠に適用
            </div>
            <p className="mt-1 text-xs text-[var(--text-muted)]">
              この設定は<strong>あなた自身が担当する枠</strong>
              にのみ適用されます（ユーザー個別設定）。
              枠の日付の「何日前まで」予約受付をオープンにするかを設定します。0 = 当日まで受付。
              この日数を過ぎた枠は「受付終了の枠・過去」へ移動し、公開フォームの候補日からも除外されます。
              休日は土日の枠に適用されます。
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bs-weekday-lead">平日の枠（月〜金）</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="bs-weekday-lead"
                  type="number"
                  min={0}
                  value={weekdayLeadDays}
                  onChange={(e) => setWeekdayLeadDays(Number(e.target.value))}
                />
                <span className="text-xs text-[var(--text-muted)] whitespace-nowrap">
                  日前まで受付
                </span>
              </div>
            </div>
            <div>
              <Label htmlFor="bs-holiday-lead">休日の枠（土日）</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="bs-holiday-lead"
                  type="number"
                  min={0}
                  value={holidayLeadDays}
                  onChange={(e) => setHolidayLeadDays(Number(e.target.value))}
                />
                <span className="text-xs text-[var(--text-muted)] whitespace-nowrap">
                  日前まで受付
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="primary"
              onClick={onSave}
              disabled={updateBriefingSettings.isPending}
            >
              {updateBriefingSettings.isPending ? '保存中...' : '保存'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const contactName = (id: string) => contacts?.find((c) => c.id === id)?.name ?? '(顧客名未確定)';

  /**
   * 確定済み Deal の開催枠を解決する (2026-08-06)。
   * confirmedBriefingScheduleId が正。2026-07-23 以前の商談には無いので、
   * briefingCandidates の status='confirmed' をフォールバックに使う。
   */
  const confirmedScheduleOf = (d: DealRecord): BriefingScheduleRecord | undefined => {
    const sid =
      d.confirmedBriefingScheduleId ||
      dealBriefingCandidates(d).find((c) => c.status === 'confirmed')?.scheduleId;
    if (!sid) return undefined;
    return schedules?.find((s) => s.id === sid);
  };

  /**
   * 開催形式ラベル (オンライン / 現地)。枠が特定できない旧データは非表示。
   * 「両方可」の枠は申込・確定の時点でどちらかに確定しているので、
   * Deal.confirmedBriefingStyle → 確定候補の selectedStyle → 枠の style の順に解決する。
   */
  const renderStyleBadge = (d: DealRecord) => {
    const s = confirmedScheduleOf(d);
    if (!s) return <span className="text-[11px] text-[var(--text-muted)]">—</span>;
    const confirmedCandidateStyle = dealBriefingCandidates(d).find(
      (c) => c.status === 'confirmed',
    )?.selectedStyle;
    const style = resolveBriefingStyle(
      s.style,
      d.confirmedBriefingStyle ?? confirmedCandidateStyle ?? null,
    );
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0">
        <Badge variant={style === 'online' ? 'sent' : 'draft'}>
          {style === 'online' ? <Video className="size-3" /> : <MapPin className="size-3" />}
          {BRIEFING_STYLE_LABEL[style]}
        </Badge>
        {style !== 'online' && s.location && (
          <span className="text-[11px] text-[var(--text-muted)] truncate" title={s.location}>
            {s.location}
          </span>
        )}
      </span>
    );
  };

  // 候補枠の担当者 (briefing_schedules.assigneeUid → admins) 表示名
  // (owner 以外でも解決できるようディレクトリ Callable 経由)
  const { data: admins } = useAdminDirectory();
  const assigneeName = (uid: string | null | undefined) => {
    if (!uid) return '';
    const a = admins?.find((x) => x.uid === uid);
    return a?.fullName || a?.displayName || '';
  };

  const renderApplicationsTab = () => {
    if (applications.length === 0) {
      return (
        <EmptyState
          icon={Clock}
          title="申込中の個別説明はありません"
          description="営業フォームから個別説明申込が送信されると、ここに表示されます。"
        />
      );
    }
    const scheduleOf = (id: string) => schedules?.find((s) => s.id === id);
    return (
      <div className="rounded-md border border-[var(--border)] overflow-hidden">
        <div className="grid grid-cols-[1fr_2fr_auto_auto] gap-3 px-4 py-2 bg-[var(--bg-elevated)] border-b border-[var(--border)] text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
          <div>氏名</div>
          <div>商談タイトル / 候補日</div>
          <div></div>
          <div></div>
        </div>
        {applications.map((d, i) => {
          const stored = dealPendingBriefingCandidates(d);
          const pending = stored.length > 0 ? stored : (fallbackCandidates?.get(d.id) ?? []);
          const isExpanded = expandedDealIds.includes(d.id);
          return (
            <div
              key={d.id}
              className={i < applications.length - 1 ? 'border-b border-[var(--border)]' : ''}
            >
              <div className="grid grid-cols-[1fr_2fr_auto_auto] gap-3 px-4 py-2.5 items-center">
                <div className="text-sm">{contactName(d.contactId)}</div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-xs text-[var(--text-secondary)] truncate">{d.title}</span>
                    {/* 確定済み商談に届いた再申込 (日程変更 / 2回目の個別説明) */}
                    {d.scheduledBriefingAt && <Badge variant="draft">再申込</Badge>}
                  </div>
                  {pending.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {pending.map((c) => {
                        const s = scheduleOf(c.scheduleId);
                        return (
                          <span
                            key={c.scheduleId}
                            className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-0.5 text-[10px] tabular-nums text-[var(--text-secondary)]"
                          >
                            <Calendar className="size-3" />
                            {s
                              ? `${s.date} ${s.startTime}〜${s.endTime} ${BRIEFING_STYLE_LABEL[resolveBriefingStyle(s.style, c.selectedStyle)]} ・ ${Math.max(1, c.participantCount ?? 1)}名${
                                  assigneeName(s.assigneeUid)
                                    ? ` ・ 担当: ${assigneeName(s.assigneeUid)}`
                                    : ''
                                }`
                              : deletedScheduleIds.has(c.scheduleId)
                                ? BRIEFING_DELETED_SLOT_LABEL
                                : c.scheduleId}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleExpanded(d.id)}
                  title={isExpanded ? '回答内容を閉じる' : 'フォーム回答内容を表示'}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? <ChevronUp /> : <ChevronDown />}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    // 顧客が選択した候補があればポップアップに自動紐付け、
                    // 無ければ従来の全枠選択ダイアログへフォールバック
                    if (pending.length > 0) {
                      setDecisionDialog({
                        open: true,
                        dealId: d.id,
                        candidates: pending,
                      });
                    } else {
                      setConfirmDialog({
                        open: true,
                        dealId: d.id,
                        salesAgentUid: d.salesAgentUid ?? null,
                      });
                    }
                  }}
                >
                  <Calendar />
                  日程確定
                </Button>
              </div>
              {isExpanded && <BriefingApplicationDetails deal={d} schedules={schedules ?? []} />}
            </div>
          );
        })}
      </div>
    );
  };

  const renderConfirmedTab = () => {
    if (confirmed.length === 0) {
      return (
        <EmptyState
          icon={CheckCircle2}
          title="確定済みの個別説明はありません"
          description="申込タブで日程を確定すると、ここに表示されます。"
        />
      );
    }
    return (
      <div className="rounded-md border border-[var(--border)] overflow-hidden">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_auto_auto] gap-3 px-4 py-2 bg-[var(--bg-elevated)] border-b border-[var(--border)] text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
          <div>氏名</div>
          <div>商談タイトル</div>
          <div>説明日</div>
          <div>開催形式</div>
          <div></div>
          <div></div>
        </div>
        {confirmed.map((d, i) => {
          const contact = contacts?.find((c) => c.id === d.contactId);
          const scheduledDate = tsToDate(d.scheduledBriefingAt);
          const mailto = contact?.defaultEmail
            ? `mailto:${contact.defaultEmail}?subject=${encodeURIComponent(
                '個別説明会のご案内',
              )}&body=${encodeURIComponent(
                'いつもお世話になっております。\n\n個別説明会のご案内をお送りいたします。\n\n日時: ' +
                  fmtDate(scheduledDate) +
                  '\n\nどうぞよろしくお願いいたします。',
              )}`
            : null;
          return (
            <div
              key={d.id}
              className={`grid grid-cols-[1fr_1.5fr_1fr_1fr_auto_auto] gap-3 px-4 py-2.5 items-center ${
                i < confirmed.length - 1 ? 'border-b border-[var(--border)]' : ''
              }`}
            >
              <div className="text-sm">{contactName(d.contactId)}</div>
              <div className="text-xs text-[var(--text-secondary)] truncate">{d.title}</div>
              <div className="text-xs tabular-nums">{fmtDate(scheduledDate)}</div>
              <div className="min-w-0">{renderStyleBadge(d)}</div>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => openRecordPage(d.id)}
              >
                <FileText />
                記録
              </Button>
              {mailto && (
                <a href={mailto} target="_blank" rel="noopener noreferrer">
                  <Button type="button" variant="ghost" size="sm">
                    <Mail />
                  </Button>
                </a>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderCompletedTab = () => {
    if (completed.length === 0) {
      return (
        <EmptyState
          icon={MessageSquare}
          title="完了済みの個別説明はありません"
          description="記録画面で記録を保存すると、ここに表示されます。"
        />
      );
    }
    return (
      <div className="rounded-md border border-[var(--border)] overflow-hidden">
        <div className="grid grid-cols-[1fr_2fr_1fr_1fr_auto] gap-3 px-4 py-2 bg-[var(--bg-elevated)] border-b border-[var(--border)] text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
          <div>氏名</div>
          <div>商談タイトル</div>
          <div>説明日</div>
          <div>開催形式</div>
          <div></div>
        </div>
        {completed.map((d, i) => (
          <div
            key={d.id}
            className={`grid grid-cols-[1fr_2fr_1fr_1fr_auto] gap-3 px-4 py-2.5 items-center ${
              i < completed.length - 1 ? 'border-b border-[var(--border)]' : ''
            }`}
          >
            <div className="text-sm flex items-center gap-2">
              {contactName(d.contactId)}
              {recordedFlags?.get(d.id) === 'not_conducted' && (
                <Badge variant="danger">未実施</Badge>
              )}
            </div>
            <div className="text-xs text-[var(--text-secondary)] truncate">{d.title}</div>
            <div className="text-xs tabular-nums">{fmtDate(tsToDate(d.scheduledBriefingAt))}</div>
            <div className="min-w-0">{renderStyleBadge(d)}</div>
            <Button type="button" variant="ghost" size="sm" onClick={() => openRecordPage(d.id)}>
              <FileText />
              詳細
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const renderScheduleTab = () => {
    if (schedulesLoading) return <Spinner />;
    if (!schedules || schedules.length === 0) {
      return (
        <EmptyState
          icon={Calendar}
          title="個別説明枠が登録されていません"
          description="「+ 枠を追加」ボタンから受付可能な日時を作成してください。"
        />
      );
    }
    const today = new Date();
    const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    // 各枠の担当者(assigneeUid)の締切設定で「受付中の枠」と「締切済(過去)の枠」に分類。
    const leadOf = (uid: string) => settingsMap?.get(uid) ?? SETTINGS_BRIEFING_DEFAULTS;
    const future = schedules.filter((s) =>
      isBriefingSlotOpen(s.date, todayYmd, leadOf(s.assigneeUid)),
    );
    const past = schedules.filter(
      (s) => !isBriefingSlotOpen(s.date, todayYmd, leadOf(s.assigneeUid)),
    );

    const renderRow = (s: BriefingScheduleRecord, last: boolean) => (
      <div
        key={s.id}
        className={`grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-3 px-4 py-2.5 items-center ${
          !last ? 'border-b border-[var(--border)]' : ''
        }`}
      >
        <div className="text-xs tabular-nums text-[var(--text-secondary)]">
          {s.date} {s.startTime}〜{s.endTime}
        </div>
        <div className="text-xs text-[var(--text-secondary)] truncate">
          {BRIEFING_STYLE_LABEL[s.style]}
          {s.location ? ` · ${s.location}` : ''}
        </div>
        <div className="text-xs tabular-nums whitespace-nowrap">
          受入{s.capacity}名
          {(s.bookedCount ?? 0) > 0 && (
            <span className="ml-1.5 text-[var(--accent-amber)] font-medium">予約済み</span>
          )}
        </div>
        <div>
          {s.isActive ? (
            <Badge variant="approved">有効</Badge>
          ) : (
            <Badge variant="default">無効</Badge>
          )}
        </div>
        {scheduleActions.edit ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setScheduleDialog({ open: true, existing: s })}
          >
            <Edit />
          </Button>
        ) : (
          <span />
        )}
        {scheduleActions.delete ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={async () => {
              if (!window.confirm('この枠を削除しますか? (予約がある場合は非アクティブ化されます)'))
                return;
              try {
                await deleteScheduleMutation.mutateAsync(s.id);
                toast.success('処理しました');
              } catch (err) {
                toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
              }
            }}
          >
            ×
          </Button>
        ) : (
          <span />
        )}
      </div>
    );

    return (
      <div className="space-y-4">
        {/* 代案F: 未確定申込のグローバルバナー */}
        {applications.length > 0 && (
          <div className="rounded-md border border-[var(--accent-amber)] bg-[var(--accent-amber-subtle)] p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Inbox className="size-4 text-[var(--accent-amber)] flex-shrink-0" />
              <div className="text-xs text-[var(--text-primary)]">
                <span className="font-semibold">未確定の申込が {applications.length} 件</span>{' '}
                あります。日程を確定すると顧客に自動通知メールが送信されます。
              </div>
            </div>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => setTab('applications')}
            >
              申込タブを開く
              <ArrowRight className="size-3" />
            </Button>
          </div>
        )}
        {future.length > 0 && (
          <div className="rounded-md border border-[var(--border)] overflow-hidden">
            <div className="px-4 py-2 bg-[var(--bg-elevated)] border-b border-[var(--border)] text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
              受付中の枠 ({future.length})
            </div>
            {future.map((s, i) => renderRow(s, i === future.length - 1))}
          </div>
        )}
        {past.length > 0 && (
          <div className="rounded-md border border-[var(--border)] overflow-hidden opacity-70">
            <div className="px-4 py-2 bg-[var(--bg-elevated)] border-b border-[var(--border)] text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
              受付終了の枠・過去 ({past.length})
            </div>
            {past.slice(0, 10).map((s, i) => renderRow(s, i === Math.min(past.length, 10) - 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Screen title="個別説明" description="申込から記録までの個別説明オペレーション。">
      {isLoading ? (
        <Spinner />
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as BriefingsTab)}>
          <TabsList>
            <TabsTrigger value="applications">申込 ({applications.length})</TabsTrigger>
            <TabsTrigger value="confirmed">確定 ({confirmed.length})</TabsTrigger>
            <TabsTrigger value="completed">完了 ({completed.length})</TabsTrigger>
            <TabsTrigger value="schedule">スケジュール</TabsTrigger>
            {canViewStatus && (
              <TabsTrigger value="status">
                実施状況
                <span className="ml-1.5 rounded border border-[var(--border)] px-1 text-[10px] font-semibold leading-4 text-[var(--text-muted)]">
                  管理者のみ
                </span>
              </TabsTrigger>
            )}
            {/* 設定は業務の流れと別なので右端に寄せる。アイコンと文字は 1 段 (2026-10-07) */}
            <TabsTrigger value="settings" className="ml-auto inline-flex items-center gap-1">
              <Settings className="size-3.5" />
              設定
            </TabsTrigger>
          </TabsList>
          <TabsContent value="applications">{renderApplicationsTab()}</TabsContent>
          <TabsContent value="confirmed">{renderConfirmedTab()}</TabsContent>
          <TabsContent value="completed">{renderCompletedTab()}</TabsContent>
          <TabsContent value="schedule">
            {scheduleActions.create && (
              <div className="flex justify-end mb-3">
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => setScheduleDialog({ open: true, existing: null })}
                >
                  <Plus />
                  枠を追加
                </Button>
              </div>
            )}
            {renderScheduleTab()}
          </TabsContent>
          <TabsContent value="settings">{renderSettingsTab()}</TabsContent>
          {canViewStatus && (
            <TabsContent value="status">
              <BriefingStatusTab focusRowKey={focusRowKey} onFocusConsumed={consumeFocusRow} />
            </TabsContent>
          )}
        </Tabs>
      )}

      <ConfirmBriefingDateDialog
        open={confirmDialog.open}
        dealId={confirmDialog.dealId}
        candidateScheduleIds={confirmDialog.candidateScheduleIds}
        salesAgentUid={confirmDialog.salesAgentUid ?? null}
        onOpenChange={(o) => setConfirmDialog((s) => ({ ...s, open: o }))}
      />
      <BriefingCandidateDecisionDialog
        open={decisionDialog.open}
        dealId={decisionDialog.dealId}
        candidates={decisionDialog.candidates}
        onOpenChange={(o) => setDecisionDialog((s) => ({ ...s, open: o }))}
      />
      <BriefingScheduleDialog
        open={scheduleDialog.open}
        existing={scheduleDialog.existing}
        onOpenChange={(o) => setScheduleDialog((s) => ({ ...s, open: o }))}
      />
    </Screen>
  );
}
